'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ChevronRight, Home } from 'lucide-react'

interface Crumb {
    label: string
    href: string
}

const labelMap: Record<string, string | null> = {
    'category': 'Categories',
    'all-stores': 'All Stores',
    'allstores': 'All Stores',
    'search': 'Search',
    'contact-us': 'Contact Us',
    'about-us': 'About Us',
    'privacy-policy': 'Privacy Policy',
    'terms-of-services': 'Terms of Service',
    'affiliate-disclosure': 'Affiliate Disclosure',
    'events': 'Events',
    'products': 'Products',
    'all-products': 'All Products',
    'page': null,
}

const formatLabel = (segment: string): string | null => {
    if (segment in labelMap) return labelMap[segment]
    if (/^\d+$/.test(segment)) return null
    return segment.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

interface Props {
    className?: string
    items?: { label: string; href?: string }[]
}

const Breadcrumb = ({ className = '', items }: Props) => {
    const pathname = usePathname()
    const segments = pathname.split('/').filter(Boolean)

    let crumbs: Crumb[] = []

    if (items && items.length > 0) {
        crumbs = items.map(it => ({ label: it.label, href: it.href || '' }))
    } else {
        crumbs = [{ label: 'Home', href: '/' }]
        segments.forEach((seg, i) => {
            const label = formatLabel(seg)
            if (!label) return
            crumbs.push({ label, href: '/' + segments.slice(0, i + 1).join('/') })
        })
    }

    if (crumbs.length <= 1) return null

    return (
        <nav
            className={`inline-flex items-center gap-2 flex-wrap text-xs px-4 py-2 rounded-full bg-white/90 backdrop-blur-md border border-slate-200/80 shadow-xs ${className}`}
            aria-label="Breadcrumb"
        >
            {crumbs.map((crumb, i) => {
                const isLast = i === crumbs.length - 1
                return (
                    <React.Fragment key={i}>
                        {i === 0 ? (
                            <Link
                                href={crumb.href}
                                className="inline-flex items-center gap-1.5 text-slate-600 hover:text-[#00a87a] font-semibold transition-colors group"
                            >
                                <Home size={13} className="text-[#00a87a] transition-transform group-hover:scale-110" />
                                <span>{crumb.label}</span>
                            </Link>
                        ) : isLast ? (
                            <span className="font-bold text-slate-900 tracking-tight">{crumb.label}</span>
                        ) : (
                            <Link
                                href={crumb.href}
                                className="text-slate-500 hover:text-[#00a87a] font-medium transition-colors"
                            >
                                {crumb.label}
                            </Link>
                        )}
                        {!isLast && <ChevronRight size={12} className="text-slate-300 flex-shrink-0" />}
                    </React.Fragment>
                )
            })}
        </nav>
    )
}

export default Breadcrumb
